export const PRIMARY_MEMBER_DEFAULT_NAME = 'Me';

export function newMemberId() {
  return `member-${Date.now().toString(36)}`;
}

export function cartLineId(memberId, dishId, mealType) {
  return `${memberId}::${dishId}::${mealType}`;
}

// Everything the checkout form collects, not just the address — `recipientName`,
// `phone` and the two free-text notes are order-level, but they live here so the
// whole form persists and reaches the backend as one `delivery` payload.
export function defaultDelivery() {
  return {
    recipientName: '', address: '', address2: '', city: '', state: '', zip: '', phone: '',
    deliveryDay: '', deliveryTime: '', orderNotes: '', deliveryNotes: '',
  };
}

export function createDefaultMember(overrides = {}) {
  return {
    id: newMemberId(),
    name: PRIMARY_MEMBER_DEFAULT_NAME,
    hasFullQuiz: false,
    profile: null,
    restrictionTags: [],
    restrictionLabels: {},
    customExclusionsText: '',
    email: '',
    ...overrides,
  };
}

/**
 * How a member's name reads in the UI. The primary member's default ("Me") is
 * shown as-is; a member somehow left nameless falls back to `ifBlank`.
 */
export function memberDisplayName(member, ifBlank = 'Unnamed') {
  return String(member?.name || '').trim() || ifBlank;
}

/**
 * Drops whatever name the first household member is carrying and restores the
 * default sentinel. Everything else about the member (restrictions, quiz profile)
 * and the rest of the persisted flow state is left alone.
 */
export function resetPrimaryMemberName(state) {
  const [primary, ...rest] = state.householdMembers || [];
  if (!primary || primary.name === PRIMARY_MEMBER_DEFAULT_NAME) return state;
  return {
    ...state,
    householdMembers: [{ ...primary, name: PRIMARY_MEMBER_DEFAULT_NAME }, ...rest],
  };
}

/**
 * Renames the first household member, but only when it still holds `expected` —
 * a name the customer typed themselves is never overwritten. Used to swap the
 * default sentinel for the signed-in customer's name and back again.
 * @returns {object} the same state object when nothing changed
 */
export function renamePrimaryMember(state, expected, name) {
  const [primary, ...rest] = state.householdMembers || [];
  if (!primary || primary.name !== expected || primary.name === name) return state;
  return { ...state, householdMembers: [{ ...primary, name }, ...rest] };
}

export function migrateFlowState(raw) {
  const base = {
    entryFlow: null,
    householdMembers: [createDefaultMember({ id: 'member-self' })],
    activeMemberId: 'member-self',
    cart: [],
    delivery: defaultDelivery(),
    cartWeek: null,
    customer: { name: '', email: '' },
    lastOrderId: null,
    pendingInvites: [],
  };

  if (!raw || typeof raw !== 'object') return base;

  if (raw.householdMembers?.length) {
    return {
      ...base,
      ...raw,
      delivery: { ...base.delivery, ...raw.delivery },
      activeMemberId: raw.activeMemberId || raw.householdMembers[0].id,
    };
  }

  const members = (raw.eaters || [{ id: 'member-self', name: PRIMARY_MEMBER_DEFAULT_NAME, restrictions: '' }]).map(
    (e, idx) => ({
      id: e.id === 'eater-1' ? 'member-self' : e.id,
      name: e.name || (idx === 0 ? PRIMARY_MEMBER_DEFAULT_NAME : ''),
      hasFullQuiz: idx === 0 && raw.hasFullQuiz,
      profile: idx === 0 ? raw.profile : null,
      restrictionTags: [],
      customExclusionsText: e.restrictions || '',
      email: '',
    }),
  );

  const cart = (raw.cart || []).map((line) => {
    const memberId = line.memberId || line.assignedEaterId || 'member-self';
    const dishId = line.dishId;
    return {
      ...line,
      lineId: line.lineId || cartLineId(memberId, dishId, line.mealType || 'unknown'),
      memberId,
    };
  });

  return {
    ...base,
    ...raw,
    delivery: { ...base.delivery, ...raw.delivery },
    householdMembers: members,
    activeMemberId: members[0]?.id || 'member-self',
    cart,
    hasFullQuiz: undefined,
    profile: undefined,
    eaters: undefined,
    profileId: undefined,
    quizAnswers: undefined,
  };
}
